import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  ShieldCheck,
  Sparkles,
  Wallet,
} from 'lucide-react'
import { SocialSignInButtons } from '@adthlete/shared-frontend'
import { loginThunk, resendConfirmationThunk } from '../redux/userSlice'
import { useAuth } from '../hooks/useAuth'
import { useCollaborations } from '../hooks/useCollaborations'
import type { AppDispatch, RootState } from '../store'
import api from '../lib/api'

const LOGOMARK = 'https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png'

const FEATURES = [
  {
    icon: Sparkles,
    title: 'Brands come to you',
    body: 'Set your rates once. Offers land in your deals, ready to accept.',
  },
  {
    icon: ShieldCheck,
    title: 'Compliance handled',
    body: 'NIL Go filing, school reports and #ad checks are part of every deal.',
  },
  {
    icon: Wallet,
    title: 'Paid through Stripe',
    body: 'Brand funds are held first, and released to you when the work is done.',
  },
]

/** What went wrong signing in with Google or Apple, by the code the backend sends back */
const OAUTH_ERRORS: Record<string, string> = {
  no_account: "There's no Athios athlete account with that email yet. Create one below.",
  wrong_app:
    'That email is registered in another Athios app (brand, manager or admin). Contact us if you need accounts in more than one app.',
  ambiguous_account:
    "More than one athlete account matches that email, so we can't tell which to use. Sign in another way.",
  email_unverified:
    "We couldn't confirm your email with that provider. Try another way to sign in.",
  account_closed: 'This account has been closed.',
  expired: 'That took a little too long. Please try again.',
  cancelled: 'Sign-in was cancelled.',
  not_configured: "That sign-in option isn't available yet.",
  invalid_token: "We couldn't verify that sign-in. Please try again.",
  bad_request: 'Something went wrong. Please try again.',
}

const fieldCls =
  'h-12 w-full rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] pl-11 pr-4 text-base text-[#111827] placeholder:text-[#9CA3AF] outline-none transition focus:border-[#84CC16] focus:ring-4 focus:ring-[#C0FD72]/40'
const primaryCls =
  'flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#C0FD72] text-base font-semibold text-[#0F172A] transition-colors hover:bg-[#B2F25E] disabled:opacity-60'
const secondaryCls =
  'flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] text-base font-medium text-[#111827] transition-colors hover:bg-[#F9FAFB] disabled:opacity-60'

const Field: React.FC<{
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
}> = ({ id, label, icon: Icon, children }) => (
  <div>
    <label htmlFor={id} className="mb-2 block text-sm font-medium text-[#374151]">
      {label}
    </label>
    <div className="relative">
      <Icon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#6B7280]" />
      {children}
    </div>
  </div>
)

/**
 * The athlete app's front door: sign in with a password, Google, Apple or an emailed link, or
 * create an account (email, then a password, then the profile on /onboarding). One card that
 * fills a phone; on wide screens what Athios does for athletes sits beside it.
 */
const SignIn: React.FC = () => {
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const oauthError = searchParams.get('oauth_error')
  const inviteEmail = searchParams.get('email')
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const isLoading = useSelector((state: RootState) => state.user.loading)
  const { athlete, requestMagicLink } = useAuth()
  const { refetch: refreshCampaigns } = useCollaborations()

  const [mode, setMode] = useState<'signin' | 'signup'>(
    oauthError === 'no_account' || searchParams.get('mode') === 'signup' ? 'signup' : 'signin',
  )
  const [email, setEmail] = useState(
    inviteEmail || (import.meta.env.DEV ? 'emma.davis@umich.edu' : ''),
  )
  const [password, setPassword] = useState(import.meta.env.DEV ? '321pass' : '')
  const [showPassword, setShowPassword] = useState(false)
  const [signUpEmail, setSignUpEmail] = useState(
    oauthError === 'no_account' ? (inviteEmail ?? '') : '',
  )
  const [signUpPassword, setSignUpPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [signUpStep, setSignUpStep] = useState<1 | 2>(1)
  const [error, setError] = useState<React.ReactNode>(oauthError ? OAUTH_ERRORS[oauthError] : '')
  const [notice, setNotice] = useState('')
  const [needsVerification, setNeedsVerification] = useState(false)
  const [busy, setBusy] = useState<'' | 'link' | 'signup'>('')

  useEffect(() => {
    if (athlete) {
      const safe = returnTo?.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/'
      navigate(safe, { replace: true })
    }
  }, [athlete, navigate, returnTo])

  const switchMode = (m: 'signin' | 'signup') => {
    setMode(m)
    setError('')
    setNotice('')
    setNeedsVerification(false)
  }

  const signIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    try {
      const result = await dispatch(loginThunk({ email, password })).unwrap()
      if (result) refreshCampaigns()
    } catch (err: unknown) {
      const problem = err as { email_verified?: boolean; message?: string }
      setError(problem.message || 'Sign-in failed. Please try again.')
      setNeedsVerification(problem.email_verified === false)
    }
  }

  const emailLink = async () => {
    setError('')
    setNotice('')
    if (!email) {
      setError('Enter your email address first.')
      return
    }
    setBusy('link')
    try {
      await requestMagicLink(email)
      setNotice(`Check ${email} for a link that signs you straight in.`)
    } catch (err: any) {
      if (err?.message === 'User not found') {
        const address = email
        setError(
          <span>
            There is no Athios account for {address}.{' '}
            <button
              type="button"
              className="font-semibold underline"
              onClick={() => {
                switchMode('signup')
                setSignUpEmail(address)
              }}
            >
              Create one?
            </button>
          </span>,
        )
      } else {
        setError(err?.message || "Couldn't send the link. Please try again.")
      }
    } finally {
      setBusy('')
    }
  }

  const resendConfirmation = async () => {
    try {
      await dispatch(resendConfirmationThunk(email)).unwrap()
      setError('')
      setNotice('A new confirmation email is on its way. Check your inbox.')
      setNeedsVerification(false)
    } catch (err: unknown) {
      setError((err as { message?: string }).message || "Couldn't resend the confirmation.")
    }
  }

  const signUpContinue = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setBusy('signup')
    try {
      const { data } = await api.post('/users/check-email', { email: signUpEmail })
      if (data.exists) {
        setError(
          <>
            There's already an account with that email.{' '}
            <button
              type="button"
              onClick={() => {
                setEmail(signUpEmail)
                switchMode('signin')
              }}
              className="font-semibold text-[#3F7A0A] underline"
            >
              Sign in instead
            </button>
          </>,
        )
        return
      }
      setSignUpStep(2)
    } catch {
      setError("Couldn't check that email. Please try again.")
    } finally {
      setBusy('')
    }
  }

  const signUp = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (signUpPassword.length < 6) {
      setError('Your password needs at least 6 characters.')
      return
    }
    if (signUpPassword !== confirmPassword) {
      setError("Those passwords don't match.")
      return
    }
    // The profile screen finishes the account with these
    sessionStorage.setItem('signupEmail', signUpEmail)
    sessionStorage.setItem('signupPassword', signUpPassword)
    navigate('/onboarding')
  }

  const eye = (
    <button
      type="button"
      onClick={() => setShowPassword((s) => !s)}
      aria-label={showPassword ? 'Hide password' : 'Show password'}
      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-[#6B7280] hover:text-[#111827]"
    >
      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
    </button>
  )

  return (
    <div className="min-h-screen bg-[#F4F7EF] px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto grid max-w-[1100px] items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)] lg:gap-16">
        {/* What Athios does for athletes (wide screens only) */}
        <section className="hidden lg:block">
          <p className="text-sm font-medium uppercase tracking-wider text-[#4B5563]">
            For college athletes
          </p>
          <h1 className="mt-3 text-5xl font-black leading-[1.05] tracking-tight text-[#0F172A] [text-wrap:balance]">
            Your name, your rates, your deals.
          </h1>
          <p className="mt-5 max-w-[460px] text-lg leading-relaxed text-[#4B5563]">
            Athios turns your following into brand deals you run from your phone, with the
            paperwork done for you.
          </p>
          <ul className="mt-10 space-y-7">
            {FEATURES.map((f) => (
              <li key={f.title} className="flex gap-5">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#ECFCCB] text-[#4D7C0F]">
                  <f.icon className="h-6 w-6" />
                </span>
                <span>
                  <span className="block text-lg font-bold text-[#0F172A]">{f.title}</span>
                  <span className="block text-base text-[#4B5563]">{f.body}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* The card */}
        <section className="w-full">
          <div className="mx-auto w-full max-w-[460px] rounded-[28px] bg-[#FFFFFF] px-5 py-8 shadow-[0_20px_60px_rgba(16,24,40,0.08)] sm:px-8 sm:py-10">
            <div className="flex justify-center">
              <img src={LOGOMARK} alt="Athios" className="h-11 w-11" />
            </div>
            <h2 className="mt-5 text-center text-3xl font-black tracking-tight text-[#0F172A]">
              {mode === 'signin' ? 'Welcome back' : 'Create your account'}
            </h2>
            <p className="mt-2 text-center text-[#4B5563]">
              {mode === 'signin'
                ? 'Sign in to your athlete account'
                : signUpStep === 1
                  ? 'Start with the email brands will reach you at'
                  : 'Choose a password'}
            </p>

            {error ? (
              <div
                role="alert"
                className="mt-6 rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-sm text-[#B91C1C]"
              >
                {error}
                {needsVerification ? (
                  <button
                    type="button"
                    onClick={resendConfirmation}
                    className="ml-1 font-semibold underline"
                  >
                    Resend the confirmation email
                  </button>
                ) : null}
              </div>
            ) : null}
            {notice ? (
              <p
                role="status"
                className="mt-6 rounded-xl border border-[#D9F99D] bg-[#F7FEE7] px-4 py-3 text-sm text-[#3F6212]"
              >
                {notice}
              </p>
            ) : null}

            {mode === 'signin' ? (
              <>
                <form onSubmit={signIn} data-track-form="Sign in" className="mt-7 space-y-5">
                  <Field id="email" label="Email address" icon={Mail}>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@school.edu"
                      className={fieldCls}
                    />
                  </Field>
                  <div>
                    <Field id="password" label="Password" icon={Lock}>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className={`${fieldCls} pr-12`}
                      />
                      {eye}
                    </Field>
                    <div className="mt-2 text-right">
                      <Link
                        to="/forgot-password"
                        className="text-sm font-medium text-[#3F7A0A] underline underline-offset-2"
                      >
                        Forgot password?
                      </Link>
                    </div>
                  </div>
                  <button type="submit" disabled={isLoading} className={primaryCls}>
                    {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                    Sign in <ArrowRight className="h-5 w-5" />
                  </button>
                </form>

                <div className="my-6 flex items-center gap-3 text-xs font-medium uppercase tracking-wider text-[#6B7280]">
                  <span className="h-px flex-1 bg-[#E5E7EB]" /> Or continue with
                  <span className="h-px flex-1 bg-[#E5E7EB]" />
                </div>

                <SocialSignInButtons
                  userType="athlete"
                  returnTo={returnTo}
                  onUnavailable={(p) =>
                    setNotice(
                      `Sign in with ${p} is coming soon. Use your password or an emailed link for now.`,
                    )
                  }
                />
                <button
                  type="button"
                  onClick={emailLink}
                  disabled={busy === 'link'}
                  className={`mt-3 ${secondaryCls}`}
                >
                  {busy === 'link' ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Mail className="h-5 w-5" />
                  )}
                  Email me a sign-in link
                </button>

                <p className="mt-8 text-center text-base text-[#4B5563]">
                  New to Athios?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('signup')}
                    className="inline-flex items-center gap-1 font-semibold text-[#3F7A0A] underline underline-offset-2"
                  >
                    Create an account <ArrowRight className="h-4 w-4" />
                  </button>
                </p>
              </>
            ) : signUpStep === 1 ? (
              <>
                <form
                  onSubmit={signUpContinue}
                  data-track-form="Sign up: email"
                  className="mt-7 space-y-5"
                >
                  <Field id="signup-email" label="Email address" icon={Mail}>
                    <input
                      id="signup-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={signUpEmail}
                      onChange={(e) => setSignUpEmail(e.target.value)}
                      placeholder="you@school.edu"
                      className={fieldCls}
                    />
                  </Field>
                  <button type="submit" disabled={busy === 'signup'} className={primaryCls}>
                    {busy === 'signup' ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                    Continue <ArrowRight className="h-5 w-5" />
                  </button>
                </form>
                <p className="mt-4 text-center text-sm text-[#6B7280]">
                  Next: a password, then your sport, school and a photo.
                </p>
                <p className="mt-8 text-center text-base text-[#4B5563]">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('signin')}
                    className="font-semibold text-[#3F7A0A] underline underline-offset-2"
                  >
                    Sign in
                  </button>
                </p>
              </>
            ) : (
              <>
                <form
                  onSubmit={signUp}
                  data-track-form="Sign up: password"
                  className="mt-7 space-y-5"
                >
                  <p className="rounded-xl bg-[#F3F4F6] px-4 py-3 text-sm text-[#374151]">
                    {signUpEmail}{' '}
                    <button
                      type="button"
                      onClick={() => setSignUpStep(1)}
                      className="ml-1 font-semibold text-[#3F7A0A] underline"
                    >
                      Change
                    </button>
                  </p>
                  <Field id="signup-password" label="Password" icon={Lock}>
                    <input
                      id="signup-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      minLength={6}
                      value={signUpPassword}
                      onChange={(e) => setSignUpPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      className={`${fieldCls} pr-12`}
                    />
                    {eye}
                  </Field>
                  <Field id="confirm-password" label="Confirm password" icon={Lock}>
                    <input
                      id="confirm-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Type it again"
                      className={fieldCls}
                    />
                  </Field>
                  <button type="submit" className={primaryCls}>
                    Create account <ArrowRight className="h-5 w-5" />
                  </button>
                </form>
                <p className="mt-4 text-center text-xs text-[#6B7280]">
                  By continuing you agree to the{' '}
                  <Link to="/terms" className="underline">
                    Terms of Use and Privacy Policy
                  </Link>
                  .
                </p>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

export default SignIn
